import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { ChevronRight, ExpandMore } from '@mui/icons-material';
import { Box, ButtonBase, Collapse, SvgIcon } from '@mui/material';

const getItemMetrics = (depth) => {
  const isRoot = depth === 0;

  return {
    fontSize: isRoot ? 'var(--nav-item-root-font-size)' : 'var(--nav-item-sub-font-size)',
    fontWeight: isRoot ? 'var(--nav-item-root-font-weight)' : 'var(--nav-item-sub-font-weight)',
    lineHeight: isRoot ? 'var(--nav-item-root-line-height)' : 'var(--nav-item-sub-line-height)',
    minHeight: isRoot ? 'var(--nav-item-root-min-height)' : 'var(--nav-item-sub-min-height)',
    py: isRoot ? 'var(--nav-item-root-py)' : 'var(--nav-item-sub-py)',
  };
};

const getTreeElbowSx = ({ depth }) => {
  if (depth === 0) return {};

  return {
    '&::before': {
      borderBottomLeftRadius: '4px',
      boxShadow: 'inset 1px -1px 0 0 var(--nav-sub-tree-color)',
      content: '""',
      height: 'calc(var(--nav-tree-elbow-size) / 2)',
      left: 'calc(-1 * var(--nav-tree-indent))',
      pointerEvents: 'none',
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-100%)',
      width: 'var(--nav-tree-indent)',
      zIndex: 2,
    },
  };
};

const getButtonSx = ({ active, children, depth, hasIcon, open }) => {
  const metrics = getItemMetrics(depth);
  const isLeaf = !children;
  const showBranchPill = !!children && open;
  const showLeafPill = active && depth > 0 && isLeaf;
  const showRootLeafPill = active && depth === 0 && isLeaf;

  return {
    alignItems: 'center',
    borderRadius: 1,
    display: 'flex',
    justifyContent: 'flex-start',
    minHeight: metrics.minHeight,
    pl: hasIcon
      ? 'var(--nav-item-px)'
      : depth > 0
        ? '4px'
        : 'calc(var(--nav-item-px) + var(--nav-item-root-icon-box) + var(--nav-item-gap))',
    pr: 'var(--nav-item-px)',
    py: metrics.py,
    textAlign: 'left',
    width: '100%',
    ...(showBranchPill && {
      backgroundColor: 'var(--nav-branch-open-bg)',
    }),
    ...(showLeafPill && {
      backgroundColor: 'var(--nav-sub-item-active-bg)',
      color: 'var(--nav-item-active-color)',
    }),
    ...(showRootLeafPill && {
      backgroundColor: 'var(--nav-item-active-bg)',
    }),
    '&:hover': {
      backgroundColor: showLeafPill
        ? 'var(--nav-sub-item-active-bg)'
        : showBranchPill
          ? 'var(--nav-branch-open-bg)'
          : 'var(--nav-item-hover-bg)',
    },
  };
};

const getLabelSx = ({ active, depth, isBranch, open }) => {
  const metrics = getItemMetrics(depth);

  return {
    color: 'var(--nav-item-color)',
    flexGrow: 1,
    fontFamily: (theme) => theme.typography.fontFamily,
    fontSize: metrics.fontSize,
    fontWeight: metrics.fontWeight,
    lineHeight: metrics.lineHeight,
    whiteSpace: 'nowrap',
    ...(active && isBranch && !open && {
      color: 'var(--nav-item-icon-active-color)',
    }),
    ...(active && !isBranch && {
      color: 'var(--nav-item-active-color)',
    }),
    ...(open && isBranch && {
      color: 'var(--nav-item-active-color)',
    }),
  };
};

export const SideNavItem = (props) => {
  const {
    active,
    children,
    depth = 0,
    disabled,
    icon,
    label,
    open: openProp,
    path,
    title,
    treeLineActive = false,
  } = props;
  const [open, setOpen] = useState(!!openProp);

  useEffect(() => {
    setOpen(!!openProp);
  }, [openProp]);

  const handleToggle = useCallback(() => {
    setOpen((prevOpen) => !prevOpen);
  }, []);

  const hasIcon = depth === 0 && !!icon;
  const isBranch = !!children;
  const buttonSx = getButtonSx({ active, children, depth, hasIcon, open });
  const labelSx = getLabelSx({ active, depth, isBranch, open });
  const treeSx = getTreeElbowSx({ depth });

  const iconWrapperSx = {
    alignItems: 'center',
    color: 'var(--nav-item-icon-color)',
    display: 'inline-flex',
    flexShrink: 0,
    fontSize: 'var(--nav-item-root-icon-size)',
    height: 'var(--nav-item-root-icon-box)',
    justifyContent: 'center',
    mr: 'var(--nav-item-gap)',
    width: 'var(--nav-item-root-icon-box)',
    '& .MuiSvgIcon-root': {
      fontSize: 'var(--nav-item-root-icon-size)',
    },
    ...(active && {
      color: 'var(--nav-item-icon-active-color)',
    }),
  };

  if (children) {
    return (
      <Box
        component="li"
        sx={{
          listStyle: 'none',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            ...treeSx,
          }}
        >
          <ButtonBase
            disabled={disabled}
            onClick={handleToggle}
            sx={buttonSx}
          >
            {hasIcon && (
              <Box component="span" sx={iconWrapperSx}>
                {icon}
              </Box>
            )}
            <Box component="span" sx={labelSx}>
              {title}
            </Box>
            <SvgIcon
              sx={{
                color: 'var(--nav-item-chevron-color)',
                flexShrink: 0,
                fontSize: 16,
                ml: 'var(--nav-item-gap)',
              }}
            >
              {open ? <ExpandMore /> : <ChevronRight />}
            </SvgIcon>
          </ButtonBase>
        </Box>
        <Collapse
          in={open}
          sx={{ mt: 'var(--nav-collapse-gap)' }}
        >
          {children}
        </Collapse>
      </Box>
    );
  }

  let linkProps = undefined;

  if (path) {
    const isExternal = path.startsWith('http');

    linkProps = isExternal
      ? {
        component: 'a',
        href: path,
        rel: 'noopener noreferrer',
        target: '_blank',
      }
      : {
        component: Link,
        to: path,
      };
  }

  return (
    <Box
      component="li"
      sx={{
        listStyle: 'none',
        position: 'relative',
        zIndex: 1,
        ...treeSx,
      }}
    >
      <ButtonBase
        disabled={disabled}
        sx={buttonSx}
        {...linkProps}
      >
        {hasIcon && (
          <Box component="span" sx={iconWrapperSx}>
            {icon}
          </Box>
        )}
        <Box component="span" sx={labelSx}>
          {title}
        </Box>
        {label && (
          <Box component="span" sx={{ ml: 'var(--nav-item-gap)' }}>
            {label}
          </Box>
        )}
      </ButtonBase>
    </Box>
  );
};

SideNavItem.propTypes = {
  active: PropTypes.bool,
  children: PropTypes.node,
  depth: PropTypes.number,
  disabled: PropTypes.bool,
  icon: PropTypes.node,
  open: PropTypes.bool,
  path: PropTypes.string,
  title: PropTypes.string.isRequired,
  treeLineActive: PropTypes.bool,
};
